import { useState, useCallback } from "react";
import { Popover as BasePopover } from "@base-ui/react/popover";
import { Avatar } from "../Avatar/Avatar";
import { ChevronDownIcon } from "../../../icons/ChevronDownIcon";
import { SettingsIcon } from "../../../icons/SettingsIcon";
import { LogOutIcon } from "../../../icons/LogOutIcon";
import { LayoutDashboardIcon } from "../../../icons/LayoutDashboardIcon";
import { ShieldIcon } from "../../../icons/ShieldIcon";
import { FileTextIcon } from "../../../icons/FileTextIcon";
import styles from "./UserDropdown.module.css";

interface UserDropdownProps {
  readonly userName: string;
  readonly onLogout: () => void;
  readonly showAdminLink?: boolean;
}

export function UserDropdown({ userName, onLogout, showAdminLink }: UserDropdownProps) {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  const firstName = userName.split(/\s+/)[0];

  return (
    <BasePopover.Root open={open} onOpenChange={setOpen}>
      <BasePopover.Trigger
        className={styles.trigger}
        aria-label={userName}
      >
        <Avatar name={userName} size="sm" />
        <span className={styles.name}>{firstName}</span>
        <ChevronDownIcon
          width={14}
          height={14}
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
        />
      </BasePopover.Trigger>

      <BasePopover.Portal>
        <BasePopover.Positioner side="bottom" align="end" sideOffset={8} className={styles.positioner}>
          <BasePopover.Popup className={styles.dropdown}>
            <a
              href="/settings"
              className={styles.menuItem}
              role="menuitem"
              onClick={close}
            >
              <SettingsIcon width={16} height={16} />
              <span>Settings</span>
            </a>
            {showAdminLink ? (
              <a
                href="/admin"
                className={styles.menuItem}
                role="menuitem"
                onClick={close}
              >
                <LayoutDashboardIcon width={16} height={16} />
                <span>Admin</span>
              </a>
            ) : null}
            <div className={styles.divider} />
            <a
              href="/privacy"
              className={styles.menuItem}
              role="menuitem"
              onClick={close}
            >
              <ShieldIcon width={16} height={16} />
              <span>Privacy</span>
            </a>
            <a
              href="/terms"
              className={styles.menuItem}
              role="menuitem"
              onClick={close}
            >
              <FileTextIcon width={16} height={16} />
              <span>Terms</span>
            </a>
            <div className={styles.divider} />
            <button
              type="button"
              className={styles.menuItem}
              role="menuitem"
              onClick={() => { close(); onLogout(); }}
            >
              <LogOutIcon width={16} height={16} />
              <span>Log out</span>
            </button>
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
